import { useSupabaseClient } from '#imports';
import { pushAdminOnlyResponseMessage } from './pushAdminOnlyResponseMessage';

// Cache duration in milliseconds (12 hours)
const CACHE_DURATION = 12 * 60 * 60 * 1000;

// Store active subscriptions
const activeSubscriptions = new Map();
// Track subscription setup to prevent race conditions
const subscriptionSetupInProgress = new Set();

/**
 * Main function to get cached data with realtime updates
 */
export async function useCachedData(municipality, tableName, queryParams = {}) {
  const client = useSupabaseClient();

  // Try to get from cache first
  const cachedData = getFromCache(municipality, tableName);
  if (cachedData) {
    // Set up realtime subscription
    await setupRealtimeSubscription(client, municipality, tableName);
    return cachedData;
  }

  // If not in cache, fetch from database
  let query = client.schema(municipality).from(tableName).select(queryParams.select);

  // Only apply query methods if their parameters are not null
  if (queryParams.eqField && queryParams.eqValue) {
    query = query.eq(queryParams.eqField, queryParams.eqValue);
  }
  if (queryParams.inField && queryParams.inValues) {
    query = query.in(queryParams.inField, queryParams.inValues);
  }
  if (queryParams.limit) {
    query = query.limit(queryParams.limit);
  }
  if (queryParams.orderField && queryParams.orderDirection) {
    query = query.order(queryParams.orderField, { ascending: queryParams.orderDirection == 'ASC' });
  }

  const { data, error } = await query;

  if (!data || error) {
    const errorMessage = `${municipality} - ${tableName} data fetch error: ${error ? error.message : 'No data found'}`;
    pushAdminOnlyResponseMessage(errorMessage, false);
    console.error(errorMessage);
    return [];
  }

  // Save to cache if there are no queryParams. 
  // This is done because we dont want a custom query's cache to be used as the cache for a bulk query.
  // Eg custom query: 'select * from municipality.table where id = 1', bulk query: 'select * from municipality.table'
  if (Object.keys(queryParams).length === 0) {
    saveToCache(municipality, tableName, data);
  }

  // Set up realtime subscription
  await setupRealtimeSubscription(client, municipality, tableName);

  return data;
}

/**
 * Creates a unique key for caching based on municipality and table name
 */
function createCacheKey(municipality, tableName) {
  return `${municipality}_${tableName}`;
}

/**
 * Gets data from cache if available and not expired
 */
function getFromCache(municipality, tableName) {
  if (process.client) {
    const key = createCacheKey(municipality, tableName);
    const data = localStorage.getItem(`${key}_data`);
    const timestamp = localStorage.getItem(`${key}_timestamp`);

    if (data && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(data);
      }
    }
  }
  return null;
}

/**
 * Saves data to cache with current timestamp
 */
function saveToCache(municipality, tableName, data) {
  if (process.client) {
    const key = createCacheKey(municipality, tableName);
    localStorage.setItem(`${key}_data`, JSON.stringify(data));
    localStorage.setItem(`${key}_timestamp`, Date.now().toString());
  }
}

/**
 * Invalidates cache for a specific table
 */
function invalidateCache(municipality, tableName) {
  if (process.client) {
    const key = createCacheKey(municipality, tableName);
    localStorage.removeItem(`${key}_data`);
    localStorage.removeItem(`${key}_timestamp`);
  }
}

/**
 * Sets up realtime subscription for a table
 */
async function setupRealtimeSubscription(client, municipality, tableName, onUpdate) {
  const subscriptionKey = `${municipality}_${tableName}`;

  // If subscription setup is already in progress, wait for it to complete
  if (subscriptionSetupInProgress.has(subscriptionKey)) {
    return;
  }

  // If subscription already exists, don't create a new one
  if (activeSubscriptions.has(subscriptionKey)) {
    return;
  }

  // Mark subscription setup as in progress
  subscriptionSetupInProgress.add(subscriptionKey);

  try {
    // Create new subscription
    const subscription = client
      .channel(subscriptionKey)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: municipality,
          table: tableName
        },
        () => {
          // Invalidate cache and trigger update
          invalidateCache(municipality, tableName);
          onUpdate?.();

          // pushAdminOnlyResponseMessage(`${municipality} - ${tableName} data updated`);
          console.log(`${municipality} - ${tableName} data updated`);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Store subscription for cleanup
          activeSubscriptions.set(subscriptionKey, subscription);
        }
        // Remove from in-progress set regardless of status
        subscriptionSetupInProgress.delete(subscriptionKey);
      });
  } catch (error) {
    // Remove from in-progress set on error
    subscriptionSetupInProgress.delete(subscriptionKey);
    console.error(`Failed to setup subscription for ${subscriptionKey}:`, error);
  }
}

/**
 * Cleanup function to remove all active subscriptions
 */
export function cleanupCachedData() {
  activeSubscriptions.forEach(subscription => subscription.unsubscribe());
  activeSubscriptions.clear();
  subscriptionSetupInProgress.clear();
}

/**
 * Manually invalidate cache for a specific table
 */
export function invalidateCachedData(municipality, tableName) {
  invalidateCache(municipality, tableName);
} 